// 時區（plan/vms-web-v0.md ORG-02、§11.4 W16）：時間軸與事件時間可以用場域時區或瀏覽器時區顯示，DST 那天 23 / 25 小時要對。
import { stamp, timeStamp } from './timefmt';
// 只靠 Intl（IANA 時區資料庫在瀏覽器裡），不帶時區函式庫。

export function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function validTz(tz: string | undefined | null): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();
function parts(tz: string, ms: number): Record<string, number> {
  let f = dtfCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    dtfCache.set(tz, f);
  }
  const out: Record<string, number> = {};
  for (const p of f.formatToParts(new Date(ms))) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return out;
}

/** 那個時區在 ms 這一刻相對 UTC 的偏移（毫秒；台北 +8h）。 */
export function tzOffsetMs(tz: string, ms: number): number {
  const p = parts(tz, ms);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** 時區裡的「某日 hh:mm」→ epoch 秒（DST 跳過的那一小時往後推，重複的那一小時取第一次）。 */
export function zonedTimeToEpoch(day: string, tz: string, hour = 0, minute = 0): number {
  const [y, m, d] = day.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, hour, minute); // 把牆上時間當 UTC
  // 前後各 12 小時的偏移（DST 切換不會一天兩次）→ 兩個候選；牆上時間對得上的才算
  const before = guess - tzOffsetMs(tz, guess - 12 * 3600_000);
  const after = guess - tzOffsetMs(tz, guess + 12 * 3600_000);
  const wallOk = (t: number) => {
    const p = parts(tz, t);
    return p.hour === hour && p.minute === minute && p.day === d;
  };
  const ok = [before, after].filter(wallOk);
  if (ok.length) return Math.min(...ok) / 1000; // 重複的那一小時取第一次
  return before / 1000; // 跳過的那一小時：用切換前的偏移 = 往後推
}

/** 時區裡某日 00:00 的 epoch 秒。 */
export function zonedDayStart(day: string, tz: string): number {
  return zonedTimeToEpoch(day, tz, 0, 0);
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** 時區裡某日有幾秒（DST 開始那天 23 小時、結束那天 25 小時）。 */
export function zonedDayLength(day: string, tz: string): number {
  return zonedDayStart(addDays(day, 1), tz) - zonedDayStart(day, tz);
}

/** epoch 秒在時區裡是哪一天（YYYY-MM-DD）。 */
export function zonedDayKey(sec: number, tz: string): string {
  const p = parts(tz, sec * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

export function formatTime(sec: number, tz: string, lang: string, withSeconds = true): string {
  return timeStamp(sec * 1000, tz, withSeconds);
}

/**
 * 完整時間 `2026-10-13 22:34`（24 小時制、到分；seconds 加 `:40`），不用各語系預設格式（SPEC #vms-web-973「多語與時間格式」，P1-12）。
 * lang 不影響格式（保留參數給舊呼叫端）。
 */
export function formatDateTime(sec: number, tz: string, _lang?: string, seconds = false): string {
  return stamp(sec * 1000, { tz, seconds });
}

/** 時區縮寫 / 偏移（UI 標示用，例「GMT+8」「EDT」）。 */
export function tzLabel(tz: string, sec: number, lang = 'en-US'): string {
  try {
    const p = new Intl.DateTimeFormat(lang, { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date(sec * 1000));
    return p.find((x) => x.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}
